import { useEffect } from "react"
import { AdminAuthClient } from "@/domain/admin/client/AdminAuthClient"
import { AdminAuthStore } from "@/domain/admin/store/AdminAuthStore"
import {
  clearAccessToken,
  hasAccessToken,
  writeAccessToken,
} from "@/domain/admin/utils/AdminTokenUtils"
import type { AdminLoginInput } from "@/domain/admin/model/Admin"

/**
 * 관리자 세션 훅.
 *
 * 로그인 성공 여부만 돌려주고 화면 이동은 컴포넌트가 `useNavigate` 로 정한다
 * (`domain` 은 `components` 를 알지 못한다).
 */
export function useAdminAuth() {
  function useAdminAuthState() {
    return AdminAuthStore.useStore()
  }

  async function login(input: AdminLoginInput): Promise<boolean> {
    AdminAuthStore.set("isLoading", true)

    try {
      const result = await AdminAuthClient.login(input)
      writeAccessToken(result.accessToken)
      AdminAuthStore.set("account", result.admin)
      AdminAuthStore.set("errorMessage", "")

      return true
    } catch (error) {
      clearAccessToken()
      AdminAuthStore.set("account", null)
      AdminAuthStore.set(
        "errorMessage",
        error instanceof Error ? error.message : "로그인에 실패했습니다.",
      )

      return false
    } finally {
      AdminAuthStore.set("isChecked", true)
      AdminAuthStore.set("isLoading", false)
    }
  }

  function logout() {
    clearAccessToken()
    AdminAuthStore.set("account", null)
    AdminAuthStore.set("errorMessage", "")
    AdminAuthStore.set("isChecked", true)
  }

  /** 저장된 토큰으로 세션을 확인한다 (새로고침 후 진입) */
  async function checkSession() {
    if (!hasAccessToken()) {
      AdminAuthStore.set("account", null)
      AdminAuthStore.set("isChecked", true)
      return
    }

    try {
      const account = await AdminAuthClient.getMe()
      AdminAuthStore.set("account", account)
      AdminAuthStore.set("errorMessage", "")
    } catch {
      // 만료 · 위조 토큰은 ApiClient 가 이미 지웠다. 여기서는 세션만 비운다
      AdminAuthStore.set("account", null)
    } finally {
      AdminAuthStore.set("isChecked", true)
    }
  }

  return { useAdminAuthState, login, logout, checkSession }
}

/** 관리자 레이아웃이 진입 시 한 번 세션을 확인한다 */
export function useAdminSession() {
  const { useAdminAuthState, checkSession, logout } = useAdminAuth()
  const state = useAdminAuthState()

  useEffect(() => {
    if (state.isChecked) return

    void checkSession()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.isChecked])

  return { state, logout }
}
